import { writable } from 'svelte/store';
const DEFAULT_DURATION = 3000;
export const createToaster = (options = {}) => {
    const { subscribe, set, update } = writable([]);
    /**
     * Create toasts
     */
    function add(data, params = {}) {
        const id = params.id || crypto.randomUUID();
        const duration = params.duration ??
            (typeof options.duration === 'function'
                ? options.duration({ id, data })
                : options.duration) ??
            DEFAULT_DURATION;
        update((prev) => {
            const existing = prev.find((n) => n.id === id);
            if (!existing) {
                return [
                    ...prev,
                    {
                        id,
                        data,
                        duration,
                        time: {
                            createdAt: Date.now(),
                            timeout: duration > 0 ? setTimeout(() => clear(id), duration) : null
                        }
                    }
                ];
            }
            existing.data = data;
            existing.duration = duration;
            existing.time.reoccurredAt = Date.now();
            if (existing.time.timeout) {
                clearTimeout(existing.time.timeout);
            }
            if (existing.duration > 0) {
                existing.time.timeout = setTimeout(() => clear(id), existing.duration);
            }
            return prev;
        });
        return id;
    }
    function replace(id, data, params = {}) {
        update((prev) => prev.map((n) => {
            if (n.id !== id)
                return n;
            const duration = params.duration ??
                (typeof options.duration === 'function'
                    ? options.duration({ id, data })
                    : options.duration) ??
                DEFAULT_DURATION;
            return {
                ...n,
                data,
                duration,
                time: {
                    createdAt: Date.now(),
                    timeout: duration > 0 && !n.time.pausedAt ? setTimeout(() => clear(id), duration) : null
                }
            };
        }));
    }
    function promise(promise, params) {
        const id = add(params.loading, { id: params.id, duration: 0 });
        promise
            .then(() => {
            const duration = (typeof params.duration?.success === 'function'
                ? params.duration.success({ id, data: params.success })
                : params.duration?.success) ??
                (typeof options.duration === 'function'
                    ? options.duration({ id, data: params.success })
                    : options.duration) ??
                DEFAULT_DURATION;
            replace(id, params.success, { duration });
        })
            .catch(() => {
            const duration = (typeof params.duration?.error === 'function'
                ? params.duration.error({ id, data: params.error })
                : params.duration?.error) ??
                (typeof options.duration === 'function'
                    ? options.duration({ id, data: params.error })
                    : options.duration) ??
                DEFAULT_DURATION;
            replace(id, params.error, { duration });
        });
        return id;
    }
    /**
     * Clear toasts
     */
    function clear(id) {
        update((prev) => prev.filter((n) => n.id !== id));
    }
    function clearAll() {
        set([]);
    }
    /**
     * Manage timeouts
     */
    function _pause(toast) {
        toast.time.pausedAt = Date.now();
        if (toast.time.timeout) {
            clearTimeout(toast.time.timeout);
        }
    }
    function _resume(toast) {
        if (toast.duration > 0) {
            const elapsed = toast.time.pausedAt ? toast.time.pausedAt - toast.time.createdAt : 0;
            const remaining = toast.duration - elapsed;
            toast.time.timeout = setTimeout(() => clear(toast.id), remaining);
        }
        toast.time.pausedAt = undefined;
    }
    function pauseAll() {
        update((prev) => {
            prev.forEach(_pause);
            return prev;
        });
    }
    function resumeAll() {
        update((prev) => {
            prev.forEach(_resume);
            return prev;
        });
    }
    function pause(id) {
        update((prev) => {
            const toast = prev.find((n) => n.id === id);
            if (toast)
                _pause(toast);
            return prev;
        });
    }
    const resume = (id) => {
        update((prev) => {
            const toast = prev.find((n) => n.id === id);
            if (toast)
                _resume(toast);
            return prev;
        });
    };
    const methods = {
        subscribe,
        promise,
        replace,
        clear,
        clearAll,
        pause,
        resume,
        pauseAll,
        resumeAll
    };
    // return the 'add' function with all the other methods as properties
    return Object.assign(add, methods);
};
